import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaTimes } from 'react-icons/fa';
import U2000KeyboardKeyHint from '../../../common/U2000KeyboardKeyHint';
import { U2000AutoCompleteSearchCountFooter } from '../../../common/U2000AutoCompleteSearchCountFooter';
import { U2000_CRITERIA_LOOKUP_PAGE_SIZE } from './U2000CriteriaLookupRegistry';
import type { U2000CriteriaTag } from './u2000CriteriaTags';
import type { U2000AutoCompleteSearchResult } from '../../../../types/U2000AutoCompleteSearchResult';
import { u2000CapAutoCompleteSearchResults } from '../../../../utils/u2000CapAutoCompleteSearchResults';
import { u2000NormalizeAutoCompleteSearchResult } from '../../../../utils/u2000NormalizeAutoCompleteSearchResult';
import { u2000ShouldShowAutoCompleteDropdown } from '../../../../utils/u2000ShouldShowAutoCompleteDropdown';

export const U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN = /[\n\r\t;,]+/;

const TAG_BASE =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100';
/** Aligné U2000InputTagBase — fiche / critères en lecture seule */
const TAG_DISABLED =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-muted text-gray-600 border border-gray-200';
const CONTAINER_BASE =
  'flex flex-wrap items-center gap-1.5 w-full min-h-[46px] px-4 py-2 text-sm bg-surface border rounded-lg transition-all duration-200 border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20';
const CONTAINER_DISABLED =
  'flex flex-wrap items-center gap-1.5 w-full min-h-[46px] px-4 py-2 text-sm border rounded-lg transition-all duration-200 bg-muted text-gray-500 border-gray-200 cursor-not-allowed';

function SearchIconSpinner() {
  return (
    <svg className="h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

export type U2000TagAutocompleteProps<TItem> = {
  value: U2000CriteriaTag[];
  onChange: (tags: U2000CriteriaTag[]) => void;
  onSearch: (
    criterias: { fieldName: string; value: string }[],
  ) => Promise<TItem[] | U2000AutoCompleteSearchResult<TItem>>;
  itemToTag: (item: TItem) => U2000CriteriaTag | null;
  getTagKey: (tag: U2000CriteriaTag) => string;
  getTagLabel?: (tag: U2000CriteriaTag) => string;
  getTagTitle?: (tag: U2000CriteriaTag) => string;
  renderSuggestion?: (item: TItem) => ReactNode;
  searchCriterionField: string;
  pageSize?: number;
  maxTags?: number;
  allowFreeText?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchingLabel?: string;
  noResultsLabel?: (query: string) => string;
  onMaxTagsError?: (max: number) => void;
  onDuplicateError?: () => void;
  footerText?: string;
  removeTagAriaLabel?: (tag: U2000CriteriaTag) => string;
  /** Appelé après sélection clavier Tab pour avancer le focus */
  onAfterSelect?: () => void;
  /**
   * Longueur minimale de la saisie pour lancer la recherche / afficher la liste.
   * `0` = liste dès le focus (query vide). Défaut : `1`.
   */
  minSearchLength?: number;
  /** Loupe cliquable + F8 (aligné U2000AutoCompleteInput) — ex. ouvrir la grille critères du dialogue. */
  onOpenAdvancedSearch?: () => void;
  /** Flèche bas sans liste de suggestions ouverte : ex. passer au bloc suivant (tâches). */
  onArrowDownFromInput?: () => void;
  /** Focus sur le champ texte (ex. réinitialiser la surbrillance clavier ailleurs). */
  onInputFocus?: () => void;
};

function U2000TagAutocomplete<TItem>({
  value,
  onChange,
  onSearch,
  itemToTag,
  getTagKey,
  getTagLabel,
  getTagTitle,
  renderSuggestion,
  searchCriterionField,
  pageSize = U2000_CRITERIA_LOOKUP_PAGE_SIZE,
  maxTags = 50,
  allowFreeText = false,
  disabled = false,
  placeholder,
  searchingLabel = '…',
  noResultsLabel = (query) => query,
  onMaxTagsError,
  onDuplicateError,
  footerText,
  removeTagAriaLabel,
  onAfterSelect,
  minSearchLength = 1,
  onOpenAdvancedSearch,
  onArrowDownFromInput,
  onInputFocus,
}: U2000TagAutocompleteProps<TItem>) {
  const { t } = useTranslation();
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;
  const onOpenAdvancedSearchRef = useRef(onOpenAdvancedSearch);
  onOpenAdvancedSearchRef.current = onOpenAdvancedSearch;
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<TItem[]>([]);
  const [searchMeta, setSearchMeta] = useState<{ totalCount?: number; truncated?: boolean }>({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchSettled, setSearchSettled] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  /** Incrémente après une sélection pour relancer la recherche (ex. minSearchLength=0). */
  const [searchNonce, setSearchNonce] = useState(0);
  /** Ferme la liste après ajout d’une pastille (évite de laisser le panel ouvert au focus). */
  const [suppressDropdownAfterSelect, setSuppressDropdownAfterSelect] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);
  /** `null` = aucune recherche en cache (ex. après blur) ; permet de relancer une query vide. */
  const lastSearchedQueryRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  const labelOf = useCallback((tag: U2000CriteriaTag) => (getTagLabel ? getTagLabel(tag) : tag.label ?? tag.value), [getTagLabel]);
  const titleOf = useCallback((tag: U2000CriteriaTag) => (getTagTitle ? getTagTitle(tag) : labelOf(tag)), [getTagTitle, labelOf]);
  const ariaRemove = useCallback((tag: U2000CriteriaTag) => (removeTagAriaLabel ? removeTagAriaLabel(tag) : `Retirer ${labelOf(tag)}`), [removeTagAriaLabel, labelOf]);

  const existingKeys = useCallback(() => new Set(value.map((tag) => getTagKey(tag))), [value, getTagKey]);

  const tryAddTag = useCallback(
    (tag: U2000CriteriaTag): boolean => {
      const key = getTagKey(tag);
      if (!key) return false;
      if (value.length >= maxTags) {
        onMaxTagsError?.(maxTags);
        return false;
      }
      if (existingKeys().has(key)) {
        onDuplicateError?.();
        return false;
      }
      onChange([...value, { ...tag, value: key, label: tag.label ?? key, meta: tag.meta }]);
      return true;
    },
    [value, maxTags, existingKeys, onChange, getTagKey, onMaxTagsError, onDuplicateError],
  );

  const addFreeText = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      tryAddTag({ value: trimmed, label: trimmed });
    },
    [tryAddTag],
  );

  const addFromSuggestion = useCallback(
    (item: TItem) => {
      const tag = itemToTag(item);
      if (!tag || !getTagKey(tag)) return;
      if (!tryAddTag(tag)) return;
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      requestIdRef.current += 1;
      setInputValue('');
      setSuggestions([]);
      setSearchMeta({});
      setLoadingSuggestions(false);
      setHighlightIndex(-1);
      setSearchSettled(false);
      lastSearchedQueryRef.current = null;
      setSuppressDropdownAfterSelect(true);
      if (minSearchLength === 0) {
        setSearchNonce((n) => n + 1);
      }
    },
    [itemToTag, getTagKey, tryAddTag, minSearchLength],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      if (!allowFreeText) return;
      const text = e.clipboardData.getData('text');
      if (!text || !U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN.test(text)) return;
      e.preventDefault();
      const segments = text
        .split(U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN)
        .map((s) => s.trim())
        .filter(Boolean);
      let next = [...value];
      const seen = new Set(next.map(getTagKey));
      for (const segment of segments) {
        if (next.length >= maxTags) {
          onMaxTagsError?.(maxTags);
          break;
        }
        if (seen.has(segment)) continue;
        seen.add(segment);
        next = [...next, { value: segment, label: segment }];
      }
      onChange(next);
      setInputValue('');
    },
    [allowFreeText, value, maxTags, onChange, getTagKey, onMaxTagsError],
  );

  useEffect(() => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    const query = inputValue.trim();
    const belowMin = query.length < minSearchLength;
    const waitingFocusForEmpty = minSearchLength === 0 && !isFocused;
    if (disabled || belowMin || waitingFocusForEmpty) {
      lastSearchedQueryRef.current = null;
      setSuggestions([]);
      setSearchMeta({});
      setLoadingSuggestions(false);
      setSearchSettled(false);
      setHighlightIndex(-1);
      return;
    }
    setSearchSettled(false);
    debounceRef.current = window.setTimeout(async () => {
      if (lastSearchedQueryRef.current !== null && query === lastSearchedQueryRef.current) return;
      lastSearchedQueryRef.current = query;
      const requestId = ++requestIdRef.current;
      setLoadingSuggestions(true);
      try {
        const raw = await searchRef.current([
          { fieldName: searchCriterionField, value: query },
          { fieldName: 'pageSize', value: String(pageSize) },
        ]);
        if (requestId !== requestIdRef.current) return;
        const normalized = u2000CapAutoCompleteSearchResults(
          u2000NormalizeAutoCompleteSearchResult(raw),
          pageSize,
        );
        setSuggestions(normalized.results);
        setSearchMeta({ totalCount: normalized.totalCount, truncated: normalized.truncated });
        setHighlightIndex(normalized.results.length > 0 ? 0 : -1);
        setSearchSettled(true);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setSearchMeta({});
        setHighlightIndex(-1);
        setSearchSettled(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingSuggestions(false);
        }
      }
    }, 300);
    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
      requestIdRef.current += 1;
    };
  }, [inputValue, disabled, searchCriterionField, pageSize, minSearchLength, isFocused, searchNonce]);

  const commitInput = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (highlightIndex >= 0 && suggestions[highlightIndex]) {
      addFromSuggestion(suggestions[highlightIndex]);
      return;
    }
    if (allowFreeText) {
      addFreeText(trimmed);
      setInputValue('');
      lastSearchedQueryRef.current = null;
      setSuggestions([]);
      setSearchMeta({});
      setLoadingSuggestions(false);
      setHighlightIndex(-1);
    }
  }, [inputValue, highlightIndex, suggestions, addFromSuggestion, allowFreeText, addFreeText]);

  const meetsMinSearchLength = inputValue.trim().length >= minSearchLength;
  const showDropdown = u2000ShouldShowAutoCompleteDropdown({
    isFocused,
    disabled,
    queryReady: meetsMinSearchLength,
    dropdownClosed: suppressDropdownAfterSelect,
    loading: loadingSuggestions,
    suggestionCount: suggestions.length,
    searchSettled,
  });

  const openAdvancedSearch = useCallback(() => {
    if (disabled || value.length >= maxTags || !onOpenAdvancedSearchRef.current) return;
    onOpenAdvancedSearchRef.current();
    inputRef.current?.focus();
  }, [disabled, value.length, maxTags]);

  useEffect(() => {
    if (!onOpenAdvancedSearch) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'F8') return;
      const root = containerRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active == null || !root.contains(active)) return;
      e.preventDefault();
      openAdvancedSearch();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onOpenAdvancedSearch, openAdvancedSearch]);

  const advancedSearchLabel = t('keyboardHintAdvancedSearch', {
    ns: 'common',
    defaultValue: 'Recherche avancée (F8)',
  });
  const showAdvancedSearchControl = Boolean(onOpenAdvancedSearch) && !disabled && value.length < maxTags;
  const inputPadRight = showAdvancedSearchControl ? 'pr-14' : 'pr-7';

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const highlightedItem = highlightIndex >= 0 ? suggestions[highlightIndex] : undefined;

    if (e.key === 'Tab' && showDropdown && highlightedItem) {
      e.preventDefault();
      addFromSuggestion(highlightedItem);
      window.setTimeout(() => onAfterSelect?.(), 0);
      return;
    }
    if (e.key === 'Enter' || e.key === ',') {
      if (inputValue.trim()) {
        e.preventDefault();
        commitInput();
      }
      return;
    }
    if (e.key === 'Escape') {
      setSuggestions([]);
      setHighlightIndex(-1);
      return;
    }
    if (e.key === 'ArrowDown') {
      if (showDropdown && suggestions.length > 0) {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (onArrowDownFromInput) {
        e.preventDefault();
        onArrowDownFromInput();
        return;
      }
    }
    if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      return;
    }
    if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div className="w-full" ref={containerRef}>
      <div
        className={
          disabled ? CONTAINER_DISABLED : `${CONTAINER_BASE} ${isFocused ? 'border-blue-500 ring-2 ring-blue-500/20' : ''}`
        }
      >
        {value.map((tag, idx) => (
          <span
            key={`${getTagKey(tag)}-${idx}`}
            className={disabled ? TAG_DISABLED : TAG_BASE}
            title={titleOf(tag)}
            aria-label={disabled ? labelOf(tag) : ariaRemove(tag)}
          >
            <span className="truncate">{labelOf(tag)}</span>
            {!disabled && (
              <button type="button" className="rounded-full p-0.5 hover:bg-blue-200/80" onClick={() => removeTag(idx)} aria-label={ariaRemove(tag)}>
                <FaTimes className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {disabled && value.length === 0 ? <span aria-hidden>{'\u00A0'}</span> : null}
        {!disabled ? (
        <div className="relative min-w-32 flex-1">
          <input
            ref={inputRef}
            type="text"
            className={`w-full min-w-32 border-0 bg-transparent p-0 text-sm outline-hidden focus:ring-0 disabled:cursor-not-allowed ${inputPadRight}`}
            value={inputValue}
            disabled={disabled || value.length >= maxTags}
            placeholder={value.length === 0 ? placeholder : ''}
            onChange={(e) => {
              setSuppressDropdownAfterSelect(false);
              setInputValue(e.target.value);
            }}
            onFocus={() => {
              setSuppressDropdownAfterSelect(false);
              setIsFocused(true);
              onInputFocus?.();
            }}
            onBlur={(e) => {
              const related = e.relatedTarget as Node | null;
              const root = (e.currentTarget as HTMLElement).closest('.relative.min-w-32');
              if (related != null && root?.contains(related)) {
                return;
              }
              window.setTimeout(() => {
                const active = document.activeElement;
                if (active != null && root?.contains(active)) return;
                if (active === inputRef.current) return;
                setIsFocused(false);
              }, 150);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            aria-autocomplete="list"
            aria-expanded={showDropdown}
          />
          {showAdvancedSearchControl ? (
            <button
              type="button"
              tabIndex={-1}
              data-u2000-skip-grid-compact=""
              className="absolute top-1/2 right-1 z-10 flex -translate-y-1/2 flex-col items-center gap-0.5 rounded-sm text-gray-400 transition-colors hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
              onMouseDown={(e) => e.preventDefault()}
              onClick={openAdvancedSearch}
              title={advancedSearchLabel}
              aria-label={advancedSearchLabel}
            >
              {loadingSuggestions ? <SearchIconSpinner /> : <FaSearch className="h-4 w-4 text-gray-500" />}
              <U2000KeyboardKeyHint className="scale-90">F8</U2000KeyboardKeyHint>
            </button>
          ) : (
            <span
              className={`pointer-events-none absolute top-1/2 right-0 z-10 -translate-y-1/2 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
              aria-hidden
            >
              {loadingSuggestions ? <SearchIconSpinner /> : <FaSearch className="h-4 w-4" />}
            </span>
          )}
          {showDropdown && (
            <div
              className="absolute left-0 right-0 top-full z-50 mt-1 flex max-h-64 min-h-0 flex-col rounded-lg border border-gray-200 bg-white shadow-lg"
              onMouseDown={(e) => e.preventDefault()}
            >
              <ul role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
                {loadingSuggestions && <li className="px-3 py-2 text-sm text-gray-500">{searchingLabel}</li>}
                {!loadingSuggestions && suggestions.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-600">{noResultsLabel(inputValue.trim())}</li>
                )}
                {!loadingSuggestions &&
                  suggestions.map((item, idx) => (
                    <li key={idx} role="option" aria-selected={idx === highlightIndex}>
                      <button
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${idx === highlightIndex ? 'bg-blue-50' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addFromSuggestion(item)}
                      >
                        {renderSuggestion ? renderSuggestion(item) : String(itemToTag(item)?.label ?? '')}
                      </button>
                    </li>
                  ))}
              </ul>
              {!loadingSuggestions && (
                <U2000AutoCompleteSearchCountFooter
                  displayedCount={suggestions.length}
                  totalCount={searchMeta.totalCount}
                />
              )}
            </div>
          )}
        </div>
        ) : null}
      </div>
      {footerText ? <p className="mt-1 text-xs text-gray-500">{footerText}</p> : null}
    </div>
  );
}

export default U2000TagAutocomplete;
